import React, { ReactNode, createContext, forwardRef, useContext, useState } from "react";

import { Fade, FadeProps, Alert as MUIAlert, Snackbar } from "@mui/material";

const FadeTransition = forwardRef<HTMLDivElement, FadeProps>((props, ref) => (
  <Fade {...props} timeout={500} ref={ref} />
));

type AlertItem = {
  id: string;
  message: string;
  variant: "success" | "info" | "warning" | "error";
};

type AlertContextType = {
  alert: {
    success: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
    error: (message: string) => void;
  };
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  const showMessage = (message: string, variant: "success" | "info" | "warning" | "error") => {
    const newAlert: AlertItem = {
      id: Date.now().toString() + Math.random().toString(36),
      message,
      variant,
    };
    setAlerts((prevAlerts) => [newAlert, ...prevAlerts]);
  };

  const alert = {
    success: (message: string) => showMessage(message, "success"),
    info: (message: string) => showMessage(message, "info"),
    warning: (message: string) => showMessage(message, "warning"),
    error: (message: string) => showMessage(message, "error"),
  };

  const removeAlert = (alertId: string) => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== alertId));
  };

  const handleClose = (alertId: string) => (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    removeAlert(alertId);
  };

  return (
    <AlertContext.Provider value={{ alert }}>
      {children}
      {alerts.map((alert, index) => (
        <Snackbar
          key={alert.id}
          open={true}
          autoHideDuration={5000}
          onClose={handleClose(alert.id)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={FadeTransition}
          sx={{ top: `${8 + index * 60}px !important` }} // Stack alerts vertically
        >
          <MUIAlert
            onClose={handleClose(alert.id)}
            severity={alert.variant}
            variant="filled"
            sx={{ whiteSpace: "pre-line" }}
          >
            {alert.message}
          </MUIAlert>
        </Snackbar>
      ))}
    </AlertContext.Provider>
  );
};
